import React, { useState, useEffect, useCallback } from 'react';
import { Input, Button, Row, Col, Form, FormGroup, Label } from 'reactstrap';
import { toast } from 'react-toastify';
import { Modal, ModalBody } from 'reactstrap';
import { useForm, Controller } from 'react-hook-form';
import axios from 'axios';
import DialerService from '../service';
import ReactButton from 'components/Buttons/ReactButton';
import FlexSelect from 'components/Inputs/FlexSelect';
import { sheetReader } from 'utils/fuctions/sheetReader.js';
import { generateSheetHeaders } from 'utils/fuctions/generateSheetHeaders.js';
import { defaultFields } from '../utils/defaultFields';
import { defaultOptionalFields } from '../utils/defaultOptionalFields';
import { generateTableFromListOfObjects } from '../utils/generateTableFromListOfObjects';

import {
  AddMorePhones,
  CheckboxContainer,
  CheckboxLabel,
  Item,
  StyledCheckbox,
  ModalContentWrapper
} from './components/styled';
import { ReactSortable } from 'react-sortablejs';
import { FaFileImport, FaPlus, FaRegFileAlt } from 'react-icons/fa';
import { Separator } from 'components/Separator';
import { FileView } from './components/fileView';
import { FlexChip } from 'components/FlexChip';
import { DragAndDrop } from 'components/DragAndDrop';
import ReactLoader from 'components/Loader';
import { useSelector } from 'react-redux';
import { RejectedNumbersTable } from './components/rejectedNumbersTable';
import ModalConfirmation from 'components/Modal/ConfirmationModal';

const MailingModal = ({
  dialerId,
  queueId,
  importType,
  integrationUrl,
  disabled
}) => {
  const { control, handleSubmit, setValue, watch } = useForm();
  const [CSVFile, setCSVFile] = useState(null);
  const [mallingData, setMallingData] = useState({});
  const [columns, setColumns] = useState([]);
  const [hasHeader, setHasHeader] = useState(true);
  const [fields, setFields] = useState(defaultFields);
  const [optionalFields, setOptionalFields] = useState(defaultOptionalFields);
  const [isOpen, setIsOpen] = useState(false);
  const [stateOrder, setStateOrder] = useState([]);
  const [phoneFormatConfirmed, setPhoneFormatConfirmed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [openModalConfirmation, setOpenModalConfirmation] = useState(false);
  const [dataToImport, setDataToImport] = useState();
  const [messageFieldsInvalid, setMessageFieldsInvalid] = useState('');
  const [replaceExistingContacts, setReplaceExistingContacts] = useState(false);

  /**
   * @type {[
   *   Array<import('./components/rejectedNumbersTable').RejectedNumber>,
   *   React.Dispatch<React.SetStateAction<Array<import('./components/rejectedNumbersTable').RejectedNumber>>>
   * ]}
   */
  const [rejectedNumbers, setRejectedNumbers] = useState([]);

  const systemConfiguration = useSelector((state) => state.systemConfiguration);

  const selectedFields = fields.map((field) => {
    return watch(field.name);
  });
  const ordenationField = watch('ordenationField');

  useEffect(() => {
    const csvData = mallingData.parsedCsv;
    const orderFields = [];
    const orderAux = [];

    if (csvData && ordenationField) {
      const fieldIndex = csvData[0].indexOf(ordenationField);

      for (let i = 1; i < csvData.length; i++) {
        const fieldValue = csvData[i][fieldIndex];
        const isNewGroup = !orderAux.includes(fieldValue);

        if (isNewGroup) {
          orderAux.push(fieldValue);
          orderFields.push({ id: i, name: fieldValue });
        }
      }

      setStateOrder(orderFields);
    }
  }, [ordenationField, mallingData]);

  useEffect(() => {
    if (importType === 'integration' && isOpen) {
      const fetchIntegrationUrlData = async () => {
        const { url, method, headers: headersRaw } = integrationUrl;
        const headers = headersRaw
          .split(';')
          .map((e) => e.split(': '))
          .reduce((prev, [k, v]) => {
            prev[k] = v;

            return prev;
          }, {});

        const res = await axios.request({ url, method, headers });

        return res;
      };

      fetchIntegrationUrlData()
        .then((res) => {
          const table = generateTableFromListOfObjects(
            res.data.data || res.data
          );

          setMallingData({
            parsedCsv: table,
            dialer_id: dialerId,
            queue_id: queueId
          });
          setColumns([...table[0].map((e) => ({ col: e }))]);
          setCSVFile(res);
        })
        .finally(() => setLoading(false));
    }
  }, [isOpen, dialerId, importType, queueId, integrationUrl]);

  useEffect(() => {
    const savedConfirmation = localStorage.getItem('phoneFormatConfirmed');
    if (savedConfirmation) {
      setPhoneFormatConfirmed(JSON.parse(savedConfirmation));
    }
  }, []);

  const changeFile = useCallback(
    /**
     * @param {File[]} acceptedFiles - Array contendo todos os arquivos aceitos
     * @param {import('react-dropzone').FileRejection[]} rejectedFiles - Array contendo todos os arquivos rejeitados, com detalhes de erro
     * @param {import('react-dropzone').DropEvent} _event - Objeto do evento
     */
    // eslint-disable-next-line no-unused-vars
    async (acceptedFiles, rejectedFiles, _event) => {
      setHasHeader(true);
      setFields(defaultFields);
      setOptionalFields(defaultOptionalFields);

      if (rejectedFiles.length) {
        /** @type {Object<string, File[]>} */
        const rejectedFilesByType = {
          invalidType: [],
          tooLarge: [],
          tooSmall: [],
          tooManyFiles: []
        };

        // Um arquivo pode ter mais de um código de erro, inviabilizando a utilização de switch statement
        rejectedFiles.forEach((file) => {
          const fileErrorCodes = file.errors.map((error) => error.code);

          if (fileErrorCodes.includes('file-invalid-type'))
            rejectedFilesByType.invalidType.push(file.file);

          if (fileErrorCodes.includes('file-too-large'))
            rejectedFilesByType.tooLarge.push(file.file);

          if (fileErrorCodes.includes('file-too-small'))
            rejectedFilesByType.tooSmall.push(file.file);

          if (fileErrorCodes.includes('too-many-files'))
            rejectedFilesByType.tooManyFiles.push(file.file);
        });

        const errorMessages = {
          invalidType:
            'Os arquivos a seguir não foram enviados pois são de um formato inválido',
          tooLarge:
            'Os arquivos a seguir não foram enviados pois são muito grandes',
          tooSmall:
            'Os arquivos a seguir não foram enviados pois são muito pequenos',
          tooManyFiles:
            'Você tentou enviar mais arquivos do que permitido. Os seguintes arquivos não foram enviados'
        };

        Object.keys(rejectedFilesByType).forEach((key) => {
          if (rejectedFilesByType[key].length) {
            toast(
              `${errorMessages[key]}: ${rejectedFilesByType[key]
                .map((file) => file.name)
                .join(', ')}`,
              { type: 'error', autoClose: false }
            );
          }
        });
      }

      const file = acceptedFiles[0];

      if (!file) return;

      const data = await sheetReader(file);

      // Quando um arquivo é enviado é importante que ele tenha no mínimo três colunas, pois os campos "nome", "identificador" e "número" são imprescindíveis
      const amountOfColumnsOnTheFile = data
        .trim()
        .split(/\r?\n|\r/)[0]
        .split(/\t/).length;

      if (amountOfColumnsOnTheFile < 3)
        return toast.error(
          'O arquivo que você enviou possui menos que três colunas. No mínimo três são necessárias.',
          {
            autoClose: true
          }
        );

      const parsedData = data
        .trim()
        .split(/\r?\n|\r/)
        .map((item) => item.split(/\t/))
        .filter((item) => item.length >= 3);

      setMallingData({
        parsedCsv: parsedData,
        dialer_id: dialerId,
        queue_id: queueId
      });
      setColumns([...parsedData[0].map((e) => ({ col: e }))]);
      setCSVFile(file);
    },
    []
  );

  const changeHasHeader = (e) => {
    let parsedData = [];

    if (!e.target.checked) {
      const headers = generateSheetHeaders(mallingData.parsedCsv[0].length);

      parsedData = [headers, ...mallingData.parsedCsv];
    } else {
      parsedData = mallingData.parsedCsv;

      parsedData.shift();
    }

    setMallingData({
      ...mallingData,
      parsedCsv: parsedData
    });
    setColumns([...parsedData[0].map((e) => ({ col: e }))]);
    setHasHeader(e.target.checked);
  };

  const onReplaceExistingContactsChange = (e) => {
    const isChecked = e.target.checked;
    setReplaceExistingContacts(isChecked);
    setMallingData((prev) => ({
      ...prev,
      replaceExistingContacts: isChecked
    }));
  };

  const isValidFile = () => {
    if (!CSVFile) return false;
    return true;
  };

  const resetStateRelatedWithFile = () => {
    setCSVFile(null);
    setMallingData({});
    setColumns([]);
    setHasHeader(true);
    setFields(defaultFields);
    setOptionalFields(defaultOptionalFields);
    setPhoneFormatConfirmed(false);
    setLoading(true);
    setReplaceExistingContacts(false);
  };

  function getOrder(group, priorityArray) {
    priorityArray.forEach((priority) => {
      if (priority.name === group) {
        group = priority.priority;
      }
    });

    return group;
  }

  function formatPriority(serializedData) {
    if (ordenationField && stateOrder.length > 0) {
      const newSerializedData = [];
      const priorityArray = [];

      if (stateOrder.length > 0) {
        stateOrder.forEach((order, index) => {
          priorityArray.push({
            ...order,
            priority: stateOrder.length - (index + 1)
          });
        });
      }

      const indexPriority = serializedData[0].lastIndexOf(ordenationField);

      serializedData.forEach((row, rowIndex) => {
        if (rowIndex === 0) {
          row[indexPriority] = 'prioridade';
        } else {
          row[indexPriority] = getOrder(row[indexPriority], priorityArray);
        }

        newSerializedData.push(row);
      });

      return newSerializedData;
    } else {
      return serializedData;
    }
  }

  /**
   *  @param {Object} data - Os quatro campos principais do formulário
   *  @param {String} data.identificador - A coluna que será utilizada como identificador
   *  @param {String} data.nome - A coluna que será utilizada como nome
   *  @param {String} data.numero - A coluna que será utilizada como telefone
   *  @param {String} data.whatsappNumber - A coluna que será utilizada como Whatsapp
   *  @param {String} [data.ordenationField] - A coluna que será utilizada como campo de ordenação
   *  @param {String} [data.visualizationFields] - A coluna que será utilizada como campo de visualização
   */
  const onSubmitCSV = (data) => {
    /** @type {Array<keyof data>} */
    const requiredFields = [
      'identificador',
      'nome',
      'numero',
      'whatsappNumber'
    ];

    const requiredFieldsNotFilled = requiredFields.filter(
      (campo) => !data[campo]
    );

    // Retirar a mensagem de campo necessário de todos os campos
    requiredFields.map(
      (field) =>
        (document.getElementById(`feedback-message-${field}`).innerText = '')
    );

    // Colocar a mensagem de campo necessário nos campos que não foram preenchidos
    if (requiredFieldsNotFilled.length) {
      return requiredFieldsNotFilled.map(
        (field) =>
          (document.getElementById(`feedback-message-${field}`).innerText =
            'Este campo precisa ser preenchido.')
      );
    }

    delete data.ordenationField;
    if (!isValidFile()) return;

    const colsMap = {};
    const colsVisualizationMap = [];

    const serializedData = [];
    const serializedVisualizationData = [];

    // Coletar os dados inválidos para exibição
    let index = 0;
    const invalidIndex = [];
    const invalidFields = [];
    const phoneNumbers = new Set();
    const whatsappNumbers = new Set();

    mallingData.parsedCsv.forEach((row, rowIndex) => {
      const r = [];
      const rView = [];

      if (rowIndex === 0) {
        row.forEach((col, colIndex) => {
          for (const key in data) {
            if (data[key] === col) {
              colsMap[key] = colIndex;
              r.push(key);
            }
          }

          for (const key in data.visualizationFields) {
            if (data.visualizationFields[key] === col) {
              colsVisualizationMap.push(colIndex);
              rView.push(col);
            }
          }
        });

        if (ordenationField) {
          colsMap[ordenationField] = row.indexOf(ordenationField);
          r.push(ordenationField);
        }
      } else {
        Object.keys(colsMap).forEach((colName) => {
          const col = row[colsMap[colName]];
          const fieldName = serializedData[0][colName];

          let parsedValue = col?.match(/^[^A-z]+$/g)
            ? col?.replaceAll(/[+()-.\s]/g, '')
            : col;
          r.push(parsedValue);

          // Validação de campos
          if (parsedValue?.length === 0 || parsedValue?.length > 100) {
            invalidIndex.push(index);
            invalidFields.push({
              name: row,
              index,
              message: 'Campo com mais de 100 caracteres ou vazio'
            });
          }

          // Validação específica para telefones
          if (fieldName === 'numero' || fieldName === 'whatsappNumber') {
            if (!parsedValue) {
              invalidFields.push({
                name: parsedValue,
                index,
                message: 'O telefone do cliente está vazio'
              });
              invalidIndex.push(index);
            } else {
              const cleanedNumber = parsedValue
                .replace(/\s+/g, '')
                .replace(/\D/g, '');

              if (cleanedNumber.length < 8) {
                invalidFields.push({
                  name: parsedValue,
                  index,
                  message: 'O telefone do cliente está incompleto'
                });
                invalidIndex.push(index);
              }

              if (fieldName === 'numero') {
                if (phoneNumbers.has(cleanedNumber)) {
                  invalidFields.push({
                    name: parsedValue,
                    index: index + 1,
                    message:
                      'O telefone do cliente está duplicado na coluna de telefone'
                  });
                  invalidIndex.push(index);
                } else {
                  phoneNumbers.add(cleanedNumber);
                }
              } else if (fieldName === 'whatsappNumber') {
                if (whatsappNumbers.has(cleanedNumber)) {
                  invalidFields.push({
                    name: parsedValue,
                    index: index + 1,
                    message:
                      'O telefone do cliente está duplicado na coluna de WhatsApp'
                  });
                  invalidIndex.push(index);
                } else {
                  whatsappNumbers.add(cleanedNumber);
                }
              }
            }
          }
        });

        colsVisualizationMap.forEach((e) => {
          const col = row[e];
          rView.push(
            col?.match(/^[^A-z]+$/g) ? col?.replaceAll(/[+()-.\s]/g, '') : col
          );
        });

        index++;
      }

      serializedData.push(r);
      serializedVisualizationData.push(rView);
    });

    // Montar a mensagem de erro para o modal
    let msgFields = '';
    let showModalError = false;

    if (invalidIndex.length > 0) {
      showModalError = true;

      invalidFields.forEach((invalidField) => {
        msgFields = `${msgFields} <hr style="margin: 0"/>  <p style="margin: 0"> Linha ${
          invalidField.index + 1
        }: ${invalidField.message}</p>`;
      });

      msgFields = `<h3> As seguintes informações apresentam inconsistências e não serão importadas: </h3>  ${msgFields}`;

      // Remover duplicatas da mensagem
      const uniqueErrorsSet = new Set(
        msgFields.split('<hr style="margin: 0"/>')
      );
      msgFields = Array.from(uniqueErrorsSet).join('<hr style="margin: 0"/>');
    }

    // Formatar e preparar dados para envio
    mallingData.parsedCsv = formatPriority(serializedData);
    mallingData.parsedVizualizationCsv = serializedVisualizationData;

    setDataToImport(mallingData);
    setMessageFieldsInvalid(msgFields);
    setOpenModalConfirmation(showModalError);

    // Se não houver erros, enviar diretamente
    if (!showModalError) {
      submitMallingData(mallingData);
    }
  };

  const submitMallingData = async (data) => {
    try {
      const response = await DialerService.createMalling(data);

      const toastProps = {
        autoClose: 3000,
        closeOnClick: true
      };

      const hadRejections =
        typeof response.data.data.rejected !== 'undefined' &&
        response.data.data.rejected.length > 0;

      hadRejections
        ? toast.warn('Alguns contatos não puderam ser importados', toastProps)
        : toast.success('Lista de contatos salva', toastProps);

      hadRejections
        ? setRejectedNumbers(response.data.data.rejected)
        : setRejectedNumbers([]);

      if (!hadRejections) toggleModal();
    } catch (err) {
      console.error(err);

      const message =
        (err.response && err.response.data) ||
        'Erro ao salvar lista de contatos';
      toast.error(message, {
        autoClose: 3000,
        closeOnClick: true
      });
    } finally {
      resetStateRelatedWithFile();
    }
  };

  const toggleModal = () => setIsOpen(!isOpen);
  const getEnabledFields = () => {
    let enabledFields = [];

    columns.forEach((column) => {
      let enabled = true;

      selectedFields.forEach((field) => {
        if (column.col === field) {
          enabled = false;
        }
      });

      if (enabled === true) {
        enabledFields.push(column);
      }
    });

    return enabledFields;
  };

  const handlePhoneFormatConfirm = (e) => {
    const checked = e.target.checked;
    setPhoneFormatConfirmed(checked);
    localStorage.setItem('phoneFormatConfirmed', JSON.stringify(checked));
  };

  const handleConfirmSubmit = () => {
    setOpenModalConfirmation(false);
    submitMallingData(dataToImport);
  };

  return (
    <>
      <Button
        size="sm"
        color="success"
        onClick={toggleModal}
        disabled={disabled}
      >
        <FaFileImport />
      </Button>
      <Modal
        className="modal-dialog-centered"
        isOpen={isOpen}
        toggle={() => {
          toggleModal();
          resetStateRelatedWithFile();
        }}
      >
        <div className="modal-header">
          <h6 className="modal-title" id="modal-title-default">
            Importar Lista de contatos
          </h6>
          <button
            aria-label="Close"
            className="close"
            data-dismiss="modal"
            type="button"
            onClick={() => {
              toggleModal();
              // `setRejectedNumbers` não pode ser resetada na função `resetStateRelatedWithFile` pois seria redefinida após o upload do arquivo,
              // o que causaria que não fossem renderizados os números rejeitados
              setRejectedNumbers([]);
              resetStateRelatedWithFile();
            }}
          >
            <span aria-hidden={true}>&times;</span>
          </button>
        </div>
        <ModalBody>
          {rejectedNumbers.length ? (
            <ModalContentWrapper>
              <FlexChip
                text={`Alguns contatos não puderam ser importados devido à sua má formatação no campo de número/número do Whatsapp. A formatação correta deve estar no formato DDD+Telefone Exemplo: 8532221234, 11922111234, 5191211232, (31)3232-1500`}
                type="danger"
              />
              <RejectedNumbersTable rejectedNumbers={rejectedNumbers} />
            </ModalContentWrapper>
          ) : (
            <Form onSubmit={handleSubmit(onSubmitCSV)}>
              {(() => {
                switch (importType) {
                  case 'file':
                    return (
                      <ModalContentWrapper>
                        <label className="form-control-label d-block mb-1">
                          Insira um arquivo do tipo CSV, XLS ou XLSX
                        </label>
                        <DragAndDrop
                          acceptedFileExtensions={'.csv,.xlsx,.xls'}
                          id="import_mailing"
                          name="import_mailing"
                          changeFile={changeFile}
                        />
                        {!CSVFile && (
                          <>
                            <FlexChip
                              text={`Para importar uma lista de contatos é necessário que seu arquivo possua no mínimo três colunas, de modo a satisfazer as condições a seguir: uma coluna com a identificação (ex: CPF, id), uma coluna com o nome, e uma coluna com o número discável (telefone e Whatsapp).`}
                            />
                            <FlexChip
                              text={`Os telefones inseridos no arquivo devem estar no formato DDD+Telefone
                    Exemplo: 8532221234, 11922111234, 5191211232, (31)3232-1500`}
                              type="warn"
                            />
                          </>
                        )}
                        {CSVFile && (
                          <>
                            <FileView>
                              <FaRegFileAlt />
                              {CSVFile.name}
                            </FileView>
                            <Separator />
                          </>
                        )}
                        {CSVFile && (
                          <div>
                            <FormGroup check>
                              <Label check>
                                <Input
                                  type="checkbox"
                                  name="hasHeader"
                                  checked={hasHeader}
                                  onChange={changeHasHeader}
                                />
                                Utilizar primeira linha como cabeçalho
                              </Label>
                            </FormGroup>

                            <FormGroup check>
                              <Label check>
                                <Input
                                  type="checkbox"
                                  name="replaceExistingContacts"
                                  checked={replaceExistingContacts}
                                  onChange={onReplaceExistingContactsChange}
                                />
                                Deseja substituir as importações de contatos?
                              </Label>
                            </FormGroup>

                            {fields.map((e) => (
                              <Controller
                                key={e.name}
                                control={control}
                                name={e.name}
                                defaultValue=""
                                render={(props) => (
                                  <FormGroup>
                                    <Label className="form-control-label">
                                      {e.label}
                                    </Label>
                                    <FlexSelect
                                      isClearable={true}
                                      dataOptions={columns}
                                      isMulti={false}
                                      closeMenuOnSelect={true}
                                      value={props.value}
                                      valueController={setValue}
                                      fieldName={e.name}
                                      labelName="col"
                                      valueName="col"
                                    />
                                    <span
                                      style={{
                                        color: 'red',
                                        fontWeight: '300'
                                      }}
                                      id={`feedback-message-${e.name}`}
                                    ></span>
                                  </FormGroup>
                                )}
                              />
                            ))}
                            <AddMorePhones
                              color={systemConfiguration.primary_color}
                            >
                              <Button
                                type="button"
                                size="md"
                                color="#0000"
                                onClick={() => {
                                  if (optionalFields.length > 0) {
                                    const optionalFieldsCopy = [
                                      ...optionalFields
                                    ];
                                    const field = optionalFieldsCopy.shift();

                                    setOptionalFields(optionalFieldsCopy);
                                    setFields([...fields, field]);
                                  }
                                }}
                              >
                                <FaPlus /> Associar mais um número
                              </Button>
                            </AddMorePhones>
                            <Separator />
                            {columns && (
                              <Row className="mt-4">
                                <Col>
                                  <Controller
                                    key="visualizationFields"
                                    control={control}
                                    name="visualizationFields"
                                    defaultValue=""
                                    render={(props) => (
                                      <FormGroup>
                                        <Label className="form-control-label">
                                          Campos para visualização
                                        </Label>

                                        <FlexSelect
                                          isClearable={true}
                                          dataOptions={getEnabledFields()}
                                          isMulti={true}
                                          closeMenuOnSelect={false}
                                          value={props.value}
                                          valueController={setValue}
                                          fieldName="visualizationFields"
                                          labelName="col"
                                          valueName="col"
                                        />
                                      </FormGroup>
                                    )}
                                  />
                                </Col>
                              </Row>
                            )}
                            {columns && (
                              <>
                                <Row>
                                  <Col>
                                    <Controller
                                      key="ordenationField"
                                      control={control}
                                      name="ordenationField"
                                      defaultValue=""
                                      render={(props) => (
                                        <FormGroup>
                                          <Label className="form-control-label">
                                            Campo de ordenação
                                          </Label>

                                          <FlexSelect
                                            isClearable={true}
                                            dataOptions={columns}
                                            isMulti={false}
                                            closeMenuOnSelect={true}
                                            value={props.value}
                                            valueController={setValue}
                                            fieldName="ordenationField"
                                            labelName="col"
                                            valueName="col"
                                          />
                                        </FormGroup>
                                      )}
                                    />
                                  </Col>
                                </Row>

                                {ordenationField && stateOrder && (
                                  <Row className="justify-content-md-center">
                                    <div style={{ width: '85%' }}>
                                      <ReactSortable
                                        list={stateOrder}
                                        setList={setStateOrder}
                                      >
                                        {stateOrder.map((item) => (
                                          <Item key={`${item.id}-${item.name}`}>
                                            <h5>{item.name.toUpperCase()}</h5>
                                          </Item>
                                        ))}
                                      </ReactSortable>
                                    </div>
                                  </Row>
                                )}
                              </>
                            )}
                            <FormGroup>
                              <Separator />
                              <CheckboxContainer>
                                <StyledCheckbox
                                  type="checkbox"
                                  checked={phoneFormatConfirmed}
                                  onChange={handlePhoneFormatConfirm}
                                />
                                <CheckboxLabel>
                                  Estou ciente acerca do formato correto para
                                  importação dos dados.
                                </CheckboxLabel>
                              </CheckboxContainer>
                            </FormGroup>
                          </div>
                        )}
                      </ModalContentWrapper>
                    );
                  case 'integration':
                    return loading ? (
                      <Row className="justify-content-md-center">
                        <ReactLoader />
                      </Row>
                    ) : (
                      <>
                        {fields.map((e) => (
                          <Controller
                            key={e.name}
                            control={control}
                            name={e.name}
                            defaultValue=""
                            render={(props) => (
                              <FormGroup>
                                <Label className="form-control-label">
                                  {e.label}
                                </Label>
                                <FlexSelect
                                  isClearable={true}
                                  dataOptions={columns}
                                  isMulti={false}
                                  closeMenuOnSelect={true}
                                  value={props.value}
                                  valueController={setValue}
                                  fieldName={e.name}
                                  labelName="col"
                                  valueName="col"
                                />
                              </FormGroup>
                            )}
                          />
                        ))}
                        <div>
                          <span style={{ paddingRight: '5px' }}>
                            Associar mais um número
                          </span>
                          <Button
                            type="button"
                            size="sm"
                            color="default"
                            onClick={() => {
                              if (optionalFields.length > 0) {
                                const optionalFieldsCopy = [...optionalFields];
                                const field = optionalFieldsCopy.shift();

                                setOptionalFields(optionalFieldsCopy);
                                setFields([...fields, field]);
                              }
                            }}
                          >
                            <li className="fas fa-plus"></li>
                          </Button>
                        </div>

                        {columns && (
                          <>
                            <Row>
                              <Col>
                                <Controller
                                  key="ordenationField"
                                  control={control}
                                  name="ordenationField"
                                  defaultValue=""
                                  render={(props) => (
                                    <FormGroup>
                                      <Label className="form-control-label">
                                        Campo de ordenação
                                      </Label>
                                      <FlexSelect
                                        isClearable={true}
                                        dataOptions={columns}
                                        isMulti={false}
                                        closeMenuOnSelect={true}
                                        value={props.value}
                                        valueController={setValue}
                                        fieldName="ordenationField"
                                        labelName="col"
                                        valueName="col"
                                      />
                                    </FormGroup>
                                  )}
                                />
                              </Col>
                            </Row>

                            {ordenationField && stateOrder && (
                              <Row className="justify-content-md-center">
                                <div style={{ width: '85%' }}>
                                  <ReactSortable
                                    list={stateOrder}
                                    setList={setStateOrder}
                                  >
                                    {stateOrder.map((item) => (
                                      <Item key={`${item.id}-${item.name}`}>
                                        <h5>{item.name.toUpperCase()}</h5>
                                      </Item>
                                    ))}
                                  </ReactSortable>
                                </div>
                              </Row>
                            )}
                          </>
                        )}
                      </>
                    );

                  default:
                    return null;
                }
              })()}
              <Row className="mt-5">
                <Col>
                  <ReactButton
                    btnColor="confirmation"
                    type="submit"
                    disabled={!isValidFile() || !phoneFormatConfirmed}
                  >
                    Importar
                  </ReactButton>
                  <ReactButton
                    btnColor="cancelation"
                    onClick={() => {
                      toggleModal();
                      resetStateRelatedWithFile();
                    }}
                  >
                    Cancelar
                  </ReactButton>
                </Col>
              </Row>
            </Form>
          )}
        </ModalBody>
        <ModalConfirmation
          isModalOpen={openModalConfirmation}
          onModalToggle={setOpenModalConfirmation}
          unmountOnClose={true}
          idResource={dataToImport}
          deleteResource={handleConfirmSubmit}
          message={messageFieldsInvalid}
          modalContainerClasses="modal-lg"
          messageContainsHtml={true}
        />
      </Modal>
    </>
  );
};

export default MailingModal;
